import type {
  ComponentType,
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  Ref,
} from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { equipmentDisplayName } from '@d-orca/shared/client/logistics';
import type { InventoryLevel } from '@interfaces/ILogistics';
import type {
  ISiteMapArea,
  ISiteMapEquipment,
  ISiteMapPoint,
  SiteMapEquipmentType,
} from '@interfaces/ISiteMap';
import { FloorSvg } from '@components/FloorSvg/FloorSvg';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { Tooltip, TooltipContent } from '@components/ui/Tooltip/Tooltip';
import { CloseIcon } from '@utils/icons';
import { AREA_COLOR_TOKEN, AREA_ICONS, EQUIPMENT_ACCENT_TOKEN, EQUIPMENT_CATALOG } from './siteMap.constants';
import {
  buildFoldedLevelsLookup,
  type IAreaStockRow,
  joinAreaStockFromLookup,
  labelPoint,
  pointsAttr,
  screenToPlan,
} from './siteMap.logic';
import { useStagePan } from './useStagePan';
import styles from './SiteMap.module.css';

const EQUIPMENT_ICON: Record<SiteMapEquipmentType, ComponentType<{ size?: number }>> = Object.fromEntries(
  EQUIPMENT_CATALOG.map((c) => [c.type, c.icon]),
) as Record<SiteMapEquipmentType, ComponentType<{ size?: number }>>;

/** The base plan's own viewBox — `{x, y, w, h}` in the blueprint's user space. */
interface IViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

const FALLBACK_VIEWBOX: IViewBox = { x: 0, y: 0, w: 1200, h: 1200 };

function parseViewBox(raw: string | null): IViewBox | null {
  if (!raw) return null;
  const parts = raw
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  const [x, y, w, h] = parts as [number, number, number, number];
  if (w <= 0 || h <= 0) return null;
  return { x, y, w, h };
}

/** Merges an internally-held ref with an optionally-forwarded external one so
 *  both stay attached to the same node (mirrors `FloorSvg`'s own `setRef`). */
function mergeRefs<T>(internal: { current: T | null }, external: Ref<T> | undefined) {
  return (node: T | null) => {
    internal.current = node;
    if (typeof external === 'function') external(node);
    else if (external) (external as { current: T | null }).current = node;
  };
}

export interface IMapStageProps {
  siteId: string;
  floorId: string;
  areas: ISiteMapArea[];
  equipment: ISiteMapEquipment[];
  levels: InventoryLevel[];
  selectedAreaId: string | null;
  onAreaClick: (areaId: string) => void;
  showAreas: boolean;
  showRadii: boolean;
  zoom: number;
  /** SVG-children slot Tasks 8–9 render an in-progress draw/place shape into. */
  draft?: ReactNode;
  /** Plan-space point callbacks for the draw/place tools. Both are no-ops
   *  while the caller stays in view mode (the overlay only fires
   *  area-selection then). */
  onStageClick?: (p: ISiteMapPoint) => void;
  onStageMove?: (p: ISiteMapPoint) => void;
  overlayRef?: Ref<SVGSVGElement>;
  /** Hint-chip row (Task 8's draw-mode "click to add a point" strip),
   *  rendered pinned to the stage's own bottom-left rather than the page's —
   *  keeps it in view regardless of layer toggles or the legend below. */
  hint?: ReactNode;
  /** Task 9's in-progress equipment-placement chip (icon + label at the
   *  chosen position, rendered while the radius is still being dragged out).
   *  Needs `MapStage`'s own `toPct` — a raw ReactNode slot can't position an
   *  HTML chip against the plan's viewBox, so this stays typed data instead. */
  equipmentDraft?: { position: ISiteMapPoint; type: SiteMapEquipmentType; label: string } | null;
  /** Post-create equipment editing (Task 9): pointerdown on the radius-edge
   *  hit ring / the chip itself starts a drag the CALLER tracks (window-level
   *  pointermove/pointerup) — omit a handler to disable that affordance
   *  (e.g. while a draw/place tool is active). */
  onEquipmentRadiusPointerDown?: (equipmentId: string, e: ReactPointerEvent<SVGCircleElement>) => void;
  onEquipmentChipPointerDown?: (equipmentId: string, e: ReactPointerEvent<HTMLDivElement>) => void;
  onEquipmentRemove?: (equipmentId: string) => void;
  /** True while the panel is the fixed full-viewport overlay (SiteMap's
   *  `expanded` prop, threaded through) — the stage becomes the flexed
   *  remainder of the card's column and bounds itself to the blueprint's own
   *  aspect ratio off the MEASURED available box (width = min(availableWidth,
   *  availableHeight × aspect)), so the map centers with room on both
   *  sides/top/bottom rather than overflowing past the available height.
   *  False (the default) is now ALSO bounded the same way, just off a PINNED
   *  height instead of a measured one — see the `pinnedHeight` effect below —
   *  so the stage doesn't grow taller once its row spans the full page width. */
  expanded?: boolean;
}

/**
 * Floor-plan stage: the shared `FloorSvg` blueprint plus a percent-positioned
 * overlay (area polygons, equipment radii) and an HTML chip layer (spec
 * §4.0's flat-tint area language + the platform's chip look), scrollable/
 * zoomable via the outer container. In view mode the overlay only fires
 * area-selection; `onStageClick`/`onStageMove`/`draft`/`hint` let the caller
 * (Task 8's draw mode, Task 9's place mode) drive the same stage instead.
 */
export function MapStage({
  siteId,
  floorId,
  areas,
  equipment,
  levels,
  selectedAreaId,
  onAreaClick,
  showAreas,
  showRadii,
  zoom,
  draft,
  onStageClick,
  onStageMove,
  overlayRef,
  hint,
  equipmentDraft,
  onEquipmentRadiusPointerDown,
  onEquipmentChipPointerDown,
  onEquipmentRemove,
  expanded = false,
}: IMapStageProps) {
  const { t } = useTranslation();
  const floorContainerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const stageOuterRef = useRef<HTMLDivElement | null>(null);
  /** The scroller inside the frame — what drag-to-pan scrolls. */
  const stageScrollRef = useRef<HTMLDivElement | null>(null);
  const [viewBox, setViewBox] = useState<IViewBox>(FALLBACK_VIEWBOX);

  // One folded-name lookup built from `levels`, shared by every area's stock
  // join below — `joinAreaStock` alone rebuilds that same map from scratch
  // per call, which turned into an O(areas × levels) rebuild on every render
  // once a site had more than a couple of areas.
  const levelsLookup = useMemo(() => buildFoldedLevelsLookup(levels), [levels]);
  const areaStockRowsById = useMemo(() => {
    const byAreaId = new Map<string, IAreaStockRow[]>();
    for (const area of areas) {
      byAreaId.set(area.areaId, joinAreaStockFromLookup(area.materials, levelsLookup));
    }
    return byAreaId;
  }, [areas, levelsLookup]);

  // The blueprint SVG is injected asynchronously by FloorSvg (after its own
  // fetch resolves) — a MutationObserver picks up both the initial insert and
  // any later re-fetch (floor switch), reading the real viewBox off it so our
  // overlay's coordinate system and the chip layer's x/vbWidth% positioning
  // line up with the plan pixel-for-pixel. Falls back to a 1200×1200 square
  // until (or unless) a usable viewBox shows up.
  useEffect(() => {
    const container = floorContainerRef.current;
    if (!container) return;
    const read = () => {
      const svg = container.querySelector('svg');
      setViewBox(parseViewBox(svg?.getAttribute('viewBox') ?? null) ?? FALLBACK_VIEWBOX);
    };
    read();
    const observer = new MutationObserver(read);
    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['viewBox'],
    });
    return () => observer.disconnect();
  }, [siteId, floorId]);

  // The stage's own available box (`stageOuterRef`) can be wider than it is
  // tall relative to the blueprint's aspect ratio, or the reverse — either
  // way, letting `.stageInner` just stretch to 100% width would push the
  // auto-height image past the available vertical space and force a
  // scrollbar instead of centering it. Tracking the container's own size
  // (not the window's) via `ResizeObserver` catches every cause of it
  // changing: the sidebar collapsing (`--sidebar-w` flipping), a browser
  // resize, zoom. Guarded for jsdom, which has no `ResizeObserver` — falls
  // back to `null` (100% width, the un-bounded default) rather than
  // throwing.
  //
  // Two different boxes feed this, by mode:
  //  - expanded (fullscreen overlay): the box IS the available height (the
  //    card's fixed viewport-inset column), so boundWidth reads it back off
  //    the box's own measured `rect.height`.
  //  - in-flow (now full-row): there's no fixed CSS height to read — this
  //    effect PICKS one instead, pinned to the width a HALF-width column
  //    would've had (the pre-full-row two-column layout's own share), so
  //    widening the row doesn't widen the stage too and grow it taller. The
  //    blueprint then centers with letterboxed margins in the freed-up
  //    width, the same treatment the fullscreen box already gets — just
  //    with a pinned, not measured, "available height".
  const [boundWidth, setBoundWidth] = useState<number | null>(null);
  const [pinnedHeight, setPinnedHeight] = useState<number | null>(null);
  useEffect(() => {
    const el = stageOuterRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const aspect = viewBox.h > 0 ? viewBox.w / viewBox.h : 1;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      if (expanded) {
        if (rect.height <= 0) return;
        setBoundWidth(Math.min(rect.width, rect.height * aspect));
      } else {
        const half = rect.width / 2;
        setPinnedHeight(half / aspect);
        setBoundWidth(half);
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, viewBox.w, viewBox.h]);

  const stageInnerStyle = { width: boundWidth != null ? `${boundWidth * zoom}px` : `${zoom * 100}%` };
  // Falls back to the width-driven aspect-ratio box (pre-measurement, or
  // jsdom, which has no ResizeObserver — see the effect above) until
  // `pinnedHeight` lands; the fullscreen case never sets it and keeps
  // sizing via `[data-expanded='true']`'s own `flex: 1 1 auto` instead (this
  // is a no-op there, same as before).
  const stageOuterStyle =
    !expanded && pinnedHeight != null
      ? { height: `${pinnedHeight}px` }
      : { aspectRatio: `${viewBox.w} / ${viewBox.h}` };

  const toPct = (p: ISiteMapPoint) => ({
    left: `${((p.x - viewBox.x) / viewBox.w) * 100}%`,
    top: `${((p.y - viewBox.y) / viewBox.h) * 100}%`,
  });

  const DraftIcon = equipmentDraft ? EQUIPMENT_ICON[equipmentDraft.type] : null;

  const handleStageClick = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (!onStageClick || !svgRef.current) return;
    const p = screenToPlan(svgRef.current, e.clientX, e.clientY);
    if (p) onStageClick(p);
  };
  const handleStageMove = (e: ReactMouseEvent<SVGSVGElement>) => {
    if (!onStageMove || !svgRef.current) return;
    const p = screenToPlan(svgRef.current, e.clientX, e.clientY);
    if (p) onStageMove(p);
  };

  // `onStageClick` is only ever passed for a non-view mode (draw/place/set-
  // radius, see `SiteMap.tsx`'s `handleStageClick` wiring) — its PRESENCE is
  // this component's own signal that a tool, not plain selection, owns the
  // stage's clicks right now. In that state the polygon and chip layers must
  // stop grabbing clicks themselves (CSS below), or a draw-vertex click
  // landing inside an existing area's polygon (or under an equipment chip)
  // opens the area drawer / drags the chip instead of adding a vertex.
  const interactive = Boolean(onStageClick);

  // View mode only — draw/place/set-radius already own the pointer via
  // `onStageClick`/`onStageMove` above, and their own clicks add a vertex/
  // place equipment rather than pan the container.
  const { isPanning, isPannable } = useStagePan({ containerRef: stageScrollRef, enabled: !interactive, zoom });

  return (
    <div
      ref={stageOuterRef}
      className={styles.stageOuter}
      data-mode={interactive ? 'interactive' : undefined}
      data-expanded={expanded ? 'true' : undefined}
      data-pannable={!interactive && isPannable ? 'true' : undefined}
      data-panning={isPanning ? 'true' : undefined}
      data-testid="site-map-stage"
      // In-flow (non-expanded): `pinnedHeight` (once measured — see the
      // effect above) is a FIXED px height, independent of zoom — so it
      // never grows past what a half-width column would show. At zoom > 1
      // `.stageInner` only widens (its own inline `width`), and its
      // now-taller content overflows the fixed height too; the ScrollArea
      // inside scrolls both axes, same as the fullscreen
      // case. Before it's measured (first paint, or jsdom, which has no
      // ResizeObserver), falls back to the width-driven aspect-ratio box.
      // Fullscreen: `[data-expanded='true']`'s own `flex: 1 1 auto` + the
      // column layout's `align-items: stretch` already give both axes a
      // DEFINITE size from the flex algorithm, which the aspect-ratio
      // fallback here never overrides (a no-op in that mode).
      style={stageOuterStyle}
    >
      <ScrollArea
        orientation="both"
        className={styles.stageScroll}
        viewportClassName={styles.stageViewport}
        viewportRef={stageScrollRef}
        viewportProps={{ 'data-testid': 'site-map-stage-scroll' }}
      >
        <div className={styles.stageInner} style={stageInnerStyle}>
          <div className={styles.stack}>
            <FloorSvg
              ref={floorContainerRef}
              siteId={siteId}
              floorId={floorId}
              className={styles.floorSvgWrap}
            />
            <svg
              ref={mergeRefs(svgRef, overlayRef)}
              className={styles.overlay}
              viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
              preserveAspectRatio="xMidYMid meet"
              onClick={handleStageClick}
              onMouseMove={handleStageMove}
              aria-hidden="true"
              data-testid="site-map-overlay"
            >
              {showAreas
                ? areas.map((area) => (
                    <polygon
                      key={area.areaId}
                      points={pointsAttr(area.points)}
                      data-area-id={area.areaId}
                      data-selected={selectedAreaId === area.areaId ? 'true' : undefined}
                      className={
                        selectedAreaId === area.areaId
                          ? `${styles.areaPolygon} ${styles.areaPolygonSelected}`
                          : styles.areaPolygon
                      }
                      style={{ fill: AREA_COLOR_TOKEN[area.color] }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAreaClick(area.areaId);
                      }}
                    />
                  ))
                : null}
              {showRadii
                ? equipment.map((eq) => (
                    <g key={eq.equipmentId}>
                      <circle
                        cx={eq.position.x}
                        cy={eq.position.y}
                        r={eq.radius}
                        data-equipment-id={eq.equipmentId}
                        className={styles.radiusCircle}
                        style={{
                          stroke: EQUIPMENT_ACCENT_TOKEN[eq.type],
                          fill: EQUIPMENT_ACCENT_TOKEN[eq.type],
                        }}
                      />
                      {onEquipmentRadiusPointerDown ? (
                        <circle
                          cx={eq.position.x}
                          cy={eq.position.y}
                          r={eq.radius}
                          className={styles.radiusHit}
                          data-testid={`equipment-radius-handle-${eq.equipmentId}`}
                          onPointerDown={(e) => onEquipmentRadiusPointerDown(eq.equipmentId, e)}
                        />
                      ) : null}
                    </g>
                  ))
                : null}
            </svg>
            <div className={styles.chipLayer}>
              {showAreas
                ? areas.map((area) => {
                    const rows = areaStockRowsById.get(area.areaId) ?? [];
                    const Icon = AREA_ICONS[area.icon];
                    const pos = toPct(labelPoint(area.points));
                    const tint = { '--tint': AREA_COLOR_TOKEN[area.color] } as CSSProperties;
                    return (
                      <button
                        key={area.areaId}
                        type="button"
                        className={`${styles.chipStack} ${styles.areaChip}`}
                        style={pos}
                        aria-label={area.name}
                        onClick={() => onAreaClick(area.areaId)}
                      >
                        <span className={styles.chipIconTile} style={tint}>
                          <Icon size={17} />
                        </span>
                        <span className={styles.chipNameLabel}>
                          <span className={styles.chipLabel}>{area.name}</span>
                        </span>
                        {rows.length > 0 ? (
                          // marker variant — the pill is a visual data element
                          // (like a milestone dot/progress bar), not an icon
                          // needing explanation. Names come from the area's own
                          // `materials` array (display strings as stored), not
                          // the inventory-joined rows, which can drop a name
                          // that no longer resolves against Inventory Levels.
                          <Tooltip
                            variant="marker"
                            content={<TooltipContent title={area.name} lines={area.materials} />}
                          >
                            <span
                              className={styles.chipMaterialsPill}
                              style={{ background: AREA_COLOR_TOKEN[area.color] }}
                            >
                              {t('logistics.siteMap.materialsCount', { count: rows.length })}
                            </span>
                          </Tooltip>
                        ) : null}
                      </button>
                    );
                  })
                : null}
              {equipment.map((eq) => {
                const Icon = EQUIPMENT_ICON[eq.type];
                const pos = toPct(eq.position);
                const tint = { '--tint': EQUIPMENT_ACCENT_TOKEN[eq.type] } as CSSProperties;
                // A default name reads in the viewer's language; a chosen one as typed.
                const name = equipmentDisplayName(t, eq);
                return (
                  <div
                    key={eq.equipmentId}
                    className={`${styles.chipStack} ${styles.equipmentChip}`}
                    style={pos}
                    aria-label={name}
                    onPointerDown={
                      onEquipmentChipPointerDown
                        ? (e) => onEquipmentChipPointerDown(eq.equipmentId, e)
                        : undefined
                    }
                  >
                    <span className={`${styles.chipIconTile} ${styles.chipIconTileRound}`} style={tint}>
                      <Icon size={17} />
                    </span>
                    <span className={styles.chipNameLabel}>
                      <span className={styles.chipLabel}>{name}</span>
                      {onEquipmentRemove ? (
                        <button
                          type="button"
                          className={styles.chipRemoveBtn}
                          aria-label={t('logistics.siteMap.equipment.remove')}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEquipmentRemove(eq.equipmentId);
                          }}
                        >
                          <CloseIcon size={10} />
                        </button>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Full-stage dim, above the blueprint/areas/chips, below the draft
                layer — see `.drawDim`'s comment in SiteMap.module.css. Always
                mounted (not conditionally rendered) so `[data-mode='interactive']`
                alone drives its 120ms fade in CSS rather than a mount/unmount. */}
            <div className={styles.drawDim} aria-hidden="true" />
            {draft ? (
              <svg
                className={styles.draftLayer}
                viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
                preserveAspectRatio="xMidYMid meet"
                aria-hidden="true"
              >
                {draft}
              </svg>
            ) : null}
            {/* The in-progress equipment-placement ghost chip — same "stays
                full-brightness above the dim" treatment as `.draftLayer`
                above, since it's the tool's own live input, not existing
                placed equipment. HTML (percent-positioned via `toPct`), so it
                can't live inside `.draftLayer`'s `<svg>` alongside the radius
                draft. */}
            {equipmentDraft && DraftIcon ? (
              <div className={styles.chipLayer} aria-hidden="true">
                <div className={styles.chipStack} style={toPct(equipmentDraft.position)}>
                  <span
                    className={`${styles.chipIconTile} ${styles.chipIconTileRound}`}
                    style={{ '--tint': EQUIPMENT_ACCENT_TOKEN[equipmentDraft.type] } as CSSProperties}
                  >
                    <DraftIcon size={17} />
                  </span>
                  <span className={styles.chipNameLabel}>
                    <span className={styles.chipLabel}>{equipmentDraft.label}</span>
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </ScrollArea>
      {hint ? <div className={styles.hintLayer}>{hint}</div> : null}
    </div>
  );
}
